import {Action, ReducerBuilder} from "redux-ts";
import {Reducer} from "platform/redux/Reducer";
import {OnAccountChanged, OnLoggedOut} from "core/redux/auth/AuthReduxActions";
import {DealForm, DealReduxState} from "core/redux/deal/DealReduxState";
import {
    ClearDealFormRanges,
    CloseDeals,
    CloseDealsPayload,
    CloseDealsType,
    EditDeal,
    EditDealPayload,
    EditDealType, GoingEditDeal, GoingEditDealPayload,
    GoingEditDealType, GoingOpenDeal, GoingOpenDealPayload,
    GoingOpenDealType,
    OpenDeal,
    OpenDealPayload,
    OpenDealType, RollPositionsType,
    SetDealFormInProgress,
    SetDealFormInProgressPayload,
    SetDealRanges,
    SetDealRangesPayload,
    SetDealServerError,
    SetDealServerErrorPayload, SetSymbolDetails, SetSymbolDetailsPayload,
    SetTradeManagerTab,
    SetTradeManagerTabPayload,
    SetTradeManagerView,
    SetTradeManagerViewPayload,
    UpdateDealForm,
    UpdateDealFormPayload,
} from "core/redux/deal/DealReduxActions";
import Platform from "platform/Platform";
import {ServiceType} from "enum/ServiceType";
import DealEngine from "core/engine/DealEngine";
import {Validation} from "core/util/Validation";
import TradeManagerView from "enum/TradeManagerView";
import {TradeManagerTab} from "enum/TradeManagerTab";
import {PromotionUtil} from "core/util/PromotionUtil";
import {PromotionType} from "platform/protocol/enum/PromotionType";
import {ToggleMenuType} from "core/redux/app/AppReduxActions";
import {FormFieldType} from "enum/FormFieldType";

const ValidationFields: FormFieldType[] = [
    FormFieldType.SymbolId,
    FormFieldType.Amount,
    FormFieldType.MinAmount,
    FormFieldType.OrderRate,
    FormFieldType.PositionDirection,
    FormFieldType.RateRequestType,
    FormFieldType.StopLossChecked,
    FormFieldType.StopLossValueRate,
    FormFieldType.StopLossValueAmount,
    FormFieldType.TakeProfitChecked,
    FormFieldType.TakeProfitValueRate,
    FormFieldType.TakeProfitValueAmount,
    FormFieldType.ExpirationChecked,
    FormFieldType.ExpirationDay,
    FormFieldType.ExpirationMonth,
    FormFieldType.ExpirationYear,
];

const initialState = (): DealReduxState => {
    return {
        tradeManagerView: TradeManagerView.Symbol,
        ranges: {},
        errors: {}
    };
};

export default class DealReducer extends Reducer<DealReduxState> {

    private static _instance: DealReducer;

    public static instance(): DealReducer {
        return this._instance || (this._instance = new this());
    }

    public get name(): string {
        return "deal";
    }

    private constructor() {
        super();
        const dealEngine: DealEngine = Platform.engine(ServiceType.Deal);
        this._middlewareActions.set(ToggleMenuType, dealEngine.onToggleMenu);
        this._middlewareActions.set(GoingOpenDealType, dealEngine.doGoingOpenDeal);
        this._middlewareActions.set(GoingEditDealType, dealEngine.doGoingEditDeal);
        this._middlewareActions.set(OpenDealType, dealEngine.doOpenDeal);
        this._middlewareActions.set(EditDealType, dealEngine.doEditDeal);
        this._middlewareActions.set(CloseDealsType, dealEngine.doCloseDeals);
        this._middlewareActions.set(RollPositionsType, dealEngine.doRollPositions);
    }

    protected setup(builder: ReducerBuilder<DealReduxState>): void {
        builder
            .init(initialState())
            .handle(OnLoggedOut, (state: DealReduxState, action: Action<any>) => {
                return {
                    ...initialState()
                };
            })
            .handle(OnAccountChanged, (state: DealReduxState, action: Action<any>) => {
                return {
                    ...initialState()
                };
            })
            .handle(SetTradeManagerView, (state: DealReduxState, {payload}: Action<SetTradeManagerViewPayload>) => {
                return Object.assign({}, state, {
                    tradeManagerView: payload.view
                });
            })
            .handle(SetTradeManagerTab, (state: DealReduxState, {payload}: Action<SetTradeManagerTabPayload>) => {
                return Object.assign({}, state, {
                    tradeManagerTab: payload.tab
                });
            })
            .handle(SetSymbolDetails, (state: DealReduxState, {payload}: Action<SetSymbolDetailsPayload>) => {
                return Object.assign({}, state, {
                    symbolDetails: payload.details
                });
            })
            .handle(SetDealRanges, (state: DealReduxState, {payload}: Action<SetDealRangesPayload>) => {
                let errors: {[key: string]: any} = {};
                let form: DealForm = state.form;
                if (form) {
                    if (form.UseSpreadCashBack || form.UsePositionInsurance) {
                        const hasCashBackPromo: boolean = PromotionUtil.hasPromotion(PromotionType.SPREAD_CASHBACK, payload.ranges.AvailablePromotions);
                        const hasInsurancePromo: boolean = PromotionUtil.hasPromotion(PromotionType.POSITION_INSURANCE, payload.ranges.AvailablePromotions);
                        if ((form.UseSpreadCashBack && !hasCashBackPromo) || (form.UsePositionInsurance && !hasInsurancePromo)) {
                            form = {...form};
                            form.UseSpreadCashBack = form.UseSpreadCashBack && hasCashBackPromo;
                            form.UsePositionInsurance = form.UsePositionInsurance && hasInsurancePromo;
                        }
                    }
                    errors = Validation.validateDeal(ValidationFields, form, payload.ranges)
                }
                return Object.assign({}, state, {
                    form,
                    ranges: payload.ranges,
                    errors
                });
            })
            .handle(UpdateDealForm, (state: DealReduxState, {payload}: Action<UpdateDealFormPayload>) => {
                const form: DealForm = payload.form ? Object.assign({}, state.form, payload.form) : null;
                let errors: {[key: string]: any} = {};
                if (form) {
                    errors = Validation.validateDeal(ValidationFields, form, state.ranges);
                }
                return Object.assign({}, state, {
                    form,
                    errors
                });
            })
            .handle(GoingOpenDeal, (state: DealReduxState, {payload}: Action<GoingOpenDealPayload>) => {
                return Object.assign({}, state, {
                    tradeManagerView: TradeManagerView.Symbol,
                    tradeManagerTab: TradeManagerTab.Trade
                });
            })
            .handle(GoingEditDeal, (state: DealReduxState, {payload}: Action<GoingEditDealPayload>) => {
                return Object.assign({}, state, {
                    tradeManagerView: TradeManagerView.Symbol,
                    tradeManagerTab: TradeManagerTab.Trade
                });
            })
            .handle(OpenDeal, (state: DealReduxState, action: Action<OpenDealPayload>) => {
                return Object.assign({}, state, {
                    formInProgress: true
                });
            })
            .handle(EditDeal, (state: DealReduxState, action: Action<EditDealPayload>) => {
                return Object.assign({}, state, {
                    formInProgress: true
                });
            })
            .handle(CloseDeals, (state: DealReduxState, action: Action<CloseDealsPayload>) => {
                return Object.assign({}, state, {
                    formInProgress: true
                });
            })
            .handle(SetDealFormInProgress, (state: DealReduxState, {payload}: Action<SetDealFormInProgressPayload>) => {
                return Object.assign({}, state, {
                    formInProgress: payload.formInProgress
                });
            })
            .handle(SetDealServerError, (state: DealReduxState, {payload}: Action<SetDealServerErrorPayload>) => {
                return Object.assign({}, state, {
                    serverError: payload.error
                });
            })
            .handle(ClearDealFormRanges, (state: DealReduxState, action: Action<any>) => {
                return Object.assign({}, state, {
                    form: null,
                    ranges: {},
                    errors: {},
                    formInProgress: false,
                    serverError: null
                });
            });
    }
}
